import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import AgentSkillsSection from "../components/AgentSkillsSection";
import { useTranslation } from 'react-i18next';
import {
  ShoppingCart,
  Package,
  MessageCircle,
  RefreshCw,
  BarChart3,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  ArrowLeft,
  Zap,
  Mail,
  Instagram,
} from "lucide-react";

const FEATURES = [
  {
    icon: <Package className="w-6 h-6 text-amber-500" />,
    title: "Order management",
    description:
      "Tracks orders, sends status updates, handles 'Where's my order?' instantly.",
  },
  {
    icon: <MessageCircle className="w-6 h-6 text-amber-500" />,
    title: "Customer support",
    description:
      "Answers product questions, process issues, and return requests on WhatsApp and Instagram.",
  },
  {
    icon: <RefreshCw className="w-6 h-6 text-amber-500" />,
    title: "Abandoned cart recovery",
    description:
      "Sends personalized follow-up messages to recover lost sales automatically.",
  },
  {
    icon: <BarChart3 className="w-6 h-6 text-amber-500" />,
    title: "Inventory sync",
    description:
      "Updates spreadsheets and dashboards when stock changes. Never oversell again.",
  },
];

const HOW_IT_WORKS = [
  {
    step: "01",
    title: "Connect store",
    desc: "Link Shopify, WooCommerce, or manual order tracking",
  },
  {
    step: "02",
    title: "Connect channels",
    desc: "WhatsApp Business, Instagram DM, Email",
  },
  {
    step: "03",
    title: "Set automations",
    desc: "Choose which tasks to automate: order updates, cart recovery, support",
  },
  {
    step: "04",
    title: "Go live",
    desc: "Agent handles your e-commerce operations 24/7",
  },
];

const CHANNELS = [
  { name: "WhatsApp Business", available: true },
  { name: "Instagram DM", available: true },
  { name: "Email", available: true },
  { name: "Shopify", available: false },
  { name: "WooCommerce", available: false },
];

const FAQ_ITEMS = [
  {
    question: "Does it integrate with Shopify?",
    answer:
      "Shopify integration is coming soon. Currently, it works with manual order tracking via spreadsheets and direct messages.",
  },
  {
    question: "Can it handle returns?",
    answer:
      "Yes! It can initiate return procedures and issue refund instructions based on your policies.",
  },
  {
    question: "What languages?",
    answer: "English, Portuguese, Spanish, and more.",
  },
];

function FaqItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-zinc-800 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between text-left px-6 py-4 text-zinc-200 font-medium hover:bg-white/[0.02] transition-colors"
      >
        <span className="font-mono text-sm">{question}</span>
        {open ? (
          <ChevronUp className="w-4 h-4 text-amber-500 shrink-0 ml-4" />
        ) : (
          <ChevronDown className="w-4 h-4 text-zinc-500 shrink-0 ml-4" />
        )}
      </button>
      {open && (
        <div className="px-6 pb-4 text-zinc-400 text-sm leading-relaxed">
          {answer}
        </div>
      )}
    </div>
  );
}

const TESTIMONIALS = [
  {
    quote: "Recovered 15% of abandoned carts in the first month. The agent sends personalized follow-ups while I sleep.",
    name: "Rafael Mendes",
    role: "Owner, ModaExpress (Fashion e-commerce)",
    metric: "15% cart recovery",
  },
  {
    quote: "Order tracking questions dropped 90%. Customers get instant updates on WhatsApp without me lifting a finger.",
    name: "Camila Santos",
    role: "Founder, PetShop Digital",
    metric: "90% reduction in WISMO tickets",
  },
  {
    quote: "We handle 3x more customer messages with the same team. The agent manages Instagram DMs, WhatsApp, and email simultaneously.",
    name: "Lucas Ferreira",
    role: "Director, MegaStore Online",
    metric: "3x message capacity",
  },
];

export default function Ecommerce() {
  

  const { t } = useTranslation();
  return (
    <>
      <SEOHead
        title={t('ecommerce.ai_e_commerce_agent_automate_orders_support')}
        description="AI digital workers for e-commerce. Manage orders, respond to customers, track inventory across WhatsApp, Instagram, Email. Start free trial."
        url="/marketplace/ecommerce"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://maxemployee.com/" },
            { "@type": "ListItem", position: 2, name: "Marketplace", item: "https://maxemployee.com/marketplace" },
            { "@type": "ListItem", position: 3, name: "E-commerce", item: "https://maxemployee.com/marketplace/ecommerce" }
            ]
          }
        ]}
      />
      <Header />

      <main className="min-h-screen bg-[#09090B]">
        {/* Section 1 — Hero */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 via-transparent to-transparent" />
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage:
                "radial-gradient(circle, #f59e0b 1px, transparent 1px)",
              backgroundSize: "24px 24px",
            }}
          />

          <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-24 pb-20">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 border border-zinc-800 rounded-full px-4 py-1.5 mb-8">
                <span className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-400">
                  {t('ecommerce_page.marketplace_badge')}
                </span>
              </div>

              <div className="flex items-center gap-4 mb-6">
                <ShoppingCart className="w-12 h-12 text-amber-500" />
                <h1 className="text-5xl md:text-6xl font-display font-bold text-white leading-[1.1] tracking-tight">{t('ecommerce.e_commerce_agent')}</h1>
              </div>

              <p className="mt-4 text-lg md:text-xl text-zinc-400 leading-relaxed max-w-2xl">
                {t('ecommerce_page.hero_subtitle')}
              </p>

              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/pricing?vertical=ecommerce"
                  className="bg-amber-500 hover:bg-amber-400 text-black px-8 py-3.5 font-bold uppercase tracking-wider text-sm transition-all hover:scale-105 hover:shadow-[0_0_30px_rgba(245,158,11,0.4)]"
                >
                  {t('ecommerce_page.deploy_cta')}
                </Link>
                <Link
                  to="/signup?agent=ecommerce"
                  className="border border-zinc-700 hover:border-zinc-500 text-zinc-300 px-8 py-3.5 font-mono uppercase tracking-wider text-xs transition-colors"
                >
                  {t('ecommerce_page.activate_now')}
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Breadcrumb + Back link */}
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <nav className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">
              <Link to="/" className="hover:text-amber-500 transition-colors">
                {t('nav.home')}
              </Link>
              <span className="mx-3">/</span>
              <Link
                to="/marketplace"
                className="hover:text-amber-500 transition-colors"
              >
                {t('marketplace_page.breadcrumb_marketplace')}
              </Link>
              <span className="mx-3">/</span>
              <span className="text-zinc-400">{t('ecommerce.e_commerce')}</span>
            </nav>
            <Link
              to="/marketplace"
              className="inline-flex items-center gap-2 text-zinc-500 hover:text-amber-500 font-mono text-xs uppercase tracking-wider transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              {t('ecommerce_page.back_to_marketplace')}
            </Link>
          </div>
        </div>

        {/* Section 2 — What your agent does (feature grid) */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <h2 className="text-3xl font-display font-bold text-white mb-4">{t('ecommerce.what_your_agent_does')}</h2>
          <p className="text-zinc-500 mb-12 max-w-xl">
            {t('ecommerce_page.features_subtitle')}
          </p>

          <div className="grid sm:grid-cols-2 gap-6">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                className="border border-zinc-800 rounded-xl p-8 hover:border-amber-500/30 transition-colors"
              >
                <div className="mb-4">{f.icon}</div>
                <h3 className="text-lg font-display font-bold text-white mb-2">
                  {t(`ecommerce_page.feature_${i + 1}_title`)}
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  {t(`ecommerce_page.feature_${i + 1}_desc`)}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Section 3 — How it works (timeline) */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <h2 className="text-3xl font-display font-bold text-white mb-4">{t('ecommerce.how_it_works')}</h2>
          <p className="text-zinc-500 mb-12 max-w-xl">
            {t('ecommerce_page.how_it_works_subtitle')}
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {HOW_IT_WORKS.map((item, i) => (
              <div
                key={item.step}
                className="border border-zinc-800 rounded-xl p-8 hover:border-amber-500/30 transition-colors"
              >
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-amber-500 mb-6">
                  {t('ecommerce_page.step')} {item.step}
                </div>
                <h3 className="text-lg font-display font-bold text-white mb-2">
                  {t(`ecommerce_page.step_${i + 1}_title`)}
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  {t(`ecommerce_page.step_${i + 1}_desc`)}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Section 4 — Integrated Channels */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <h2 className="text-3xl font-display font-bold text-white mb-4">{t('ecommerce.integrated_channels')}</h2>
          <p className="text-zinc-500 mb-12 max-w-xl">{t('ecommerce.connect_your_existing_channels_in_minutes')}</p>

          <div className="flex flex-wrap gap-4">
            {CHANNELS.map((ch) => (
              <div
                key={ch.name}
                className={`flex items-center gap-2 border rounded-lg px-5 py-3 ${
                  ch.available
                    ? "border-zinc-800 text-zinc-300"
                    : "border-zinc-800/50 text-zinc-600"
                }`}
              >
                {ch.available ? (
                  <CheckCircle className="w-4 h-4 text-amber-500" />
                ) : (
                  <div className="w-4 h-4 rounded-full border border-zinc-700" />
                )}
                <span className="text-sm font-mono">{ch.name}</span>
                {!ch.available && (
                  <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-600 ml-1">
                    {t('ecommerce_page.coming_soon')}
                  </span>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Section 5 — Pricing */}
        <section
          id="pricing"
          className="max-w-7xl mx-auto px-6 lg:px-12 py-20"
        >
          <h2 className="text-3xl font-display font-bold text-white mb-4">{t('ecommerce.pricing')}</h2>
          <p className="text-zinc-500 mb-12 max-w-xl">{t('ecommerce.start_automating_your_e_commerce_today')}</p>

          <div className="max-w-lg border border-amber-500/30 rounded-xl p-10 bg-gradient-to-b from-amber-500/[0.03] to-transparent">
            <div className="flex items-baseline gap-3 mb-2">
              <span className="text-4xl font-bold text-white">{t('ecommerce_page.from_price')}</span>
            </div>

            <div className="border-t border-zinc-800 pt-6 mb-8">
              <p className="text-sm font-mono uppercase tracking-wider text-zinc-500 mb-4">
                {t('ecommerce_page.included_label')}:
              </p>
              <ul className="space-y-3">
                {[
                  t('ecommerce_page.pricing_f1'),
                  t('ecommerce_page.pricing_f2'),
                  t('ecommerce_page.pricing_f3'),
                  t('ecommerce_page.pricing_f4'),
                  t('ecommerce_page.pricing_f5'),
                  t('ecommerce_page.pricing_f6'),
                ].map((item, i) => (
                  <li
                    key={item}
                    className="flex items-center gap-3 text-sm text-zinc-300"
                  >
                    <CheckCircle className="w-4 h-4 text-amber-500 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <Link
              to="/signup?agent=ecommerce"
              className="block text-center bg-amber-500 hover:bg-amber-400 text-black px-8 py-3.5 font-bold uppercase tracking-wider text-sm transition-colors"
            >
              {t('ecommerce_page.activate_ecommerce_button')}
            </Link>
          </div>
        </section>

        {/* Section 6 — Mock conversation */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <h2 className="text-3xl font-display font-bold text-white mb-4">{t('ecommerce.see_it_in_action')}</h2>
          <p className="text-zinc-500 mb-12 max-w-xl">
            {t('ecommerce_page.demo_subtitle')}
          </p>

          <div className="max-w-2xl border border-zinc-800 rounded-xl overflow-hidden">
            {/* Chat header */}
            <div className="bg-zinc-900 px-6 py-4 border-b border-zinc-800">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <ShoppingCart className="w-4 h-4 text-amber-500" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">{t('ecommerce.e_commerce_agent')}</p>
                  <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider">
                    {t('ecommerce_page.agent_status')}
                  </p>
                </div>
              </div>
            </div>

            {/* Chat messages */}
            <div className="p-6 space-y-4">
              {/* Customer message */}
              <div className="flex justify-end">
                <div className="bg-zinc-800 rounded-lg rounded-tr-none px-4 py-3 max-w-[80%]">
                  <p className="text-sm text-zinc-200">
                    {t('ecommerce_page.demo_msg_1')}
                  </p>
                </div>
              </div>

              {/* Agent message */}
              <div className="flex justify-start">
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg rounded-tl-none px-4 py-3 max-w-[80%]">
                  <p className="text-sm text-zinc-200">
                    {t('ecommerce_page.demo_msg_2')}
                  </p>
                </div>
              </div>

              {/* Customer follow-up */}
              <div className="flex justify-end">
                <div className="bg-zinc-800 rounded-lg rounded-tr-none px-4 py-3 max-w-[80%]">
                  <p className="text-sm text-zinc-200">{t('ecommerce.perfect_and_if_i_want_to_exchange_the_product')}</p>
                </div>
              </div>

              {/* Agent final response */}
              <div className="flex justify-start">
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg rounded-tl-none px-4 py-3 max-w-[80%]">
                  <p className="text-sm text-zinc-200">{t('ecommerce_page.demo_msg_4')}</p>
                </div>
              </div>
            </div>

            {/* Resolution badge */}
            <div className="px-6 pb-6">
              <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-amber-500">
                <Zap className="w-3.5 h-3.5" />
                ⚡ {t('ecommerce_page.resolution_badge')}
              </div>
            </div>
          </div>
        </section>

        {/* Section 6.4 — Testimonials */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <h2 className="text-3xl font-display font-bold text-white mb-4">{t('ecommerce.what_merchants_say')}</h2>
          <p className="text-zinc-500 mb-12 max-w-xl">{t('ecommerce.real_results_from_businesses_using_our_agents')}</p>

          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                className="border border-zinc-800 rounded-xl p-8 hover:border-amber-500/30 transition-colors flex flex-col"
              >
                <div className="inline-flex items-center gap-2 border border-amber-500/30 rounded-full px-3 py-1 mb-6 self-start">
                  <span className="text-xs font-mono uppercase tracking-wider text-amber-500">
                    {t.metric}
                  </span>
                </div>
                <blockquote className="text-zinc-300 text-sm leading-relaxed flex-1 mb-6">
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <div className="flex items-center gap-3 mt-auto pt-4 border-t border-zinc-800">
                  <div className="w-10 h-10 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                    <span className="font-display text-xs text-amber-500">
                      {t.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">{t.name}</p>
                    <p className="text-xs text-zinc-500 font-mono">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Section 6.5 — Mini Case */}
        <section className="max-w-4xl mx-auto px-6 lg:px-12 py-20">
          <div className="border border-zinc-800 bg-zinc-950/60 p-10 lg:p-14">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-4">// CASE STUDY</div>
            <h3 className="font-display text-2xl md:text-3xl uppercase text-white mb-6">{t('ecommerce.how_e_commerce_stores_use_this_agent')}</h3>
            <p className="text-zinc-300 text-lg leading-relaxed">
              {t('ecommerce_page.case_study_desc')}
            </p>
            <div className="mt-8 pt-6 border-t border-zinc-800 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                <span className="font-display text-xs text-amber-500">{t('ecommerce_page.case_study_badge', {defaultValue: 'EC'})}</span>
              </div>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t('ecommerce_page.early_adopter')}</span>
            </div>
          </div>
        </section>

        {/* Section 7 — FAQ */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <h2 className="text-3xl font-display font-bold text-white mb-4">{t('ecommerce.frequently_asked_questions')}</h2>
          <p className="text-zinc-500 mb-10 max-w-lg">{t('ecommerce.everything_you_need_to_know_about_the_e_commerce')}</p>

          <div className="max-w-3xl space-y-3">
            {FAQ_ITEMS.map((item, i) => (
              <FaqItem
                key={item.question}
                question={t(`ecommerce_page.faq_q${i + 1}`)}
                answer={t(`ecommerce_page.faq_a${i + 1}`)}
              />
            ))}
          </div>
        </section>

        {/* Section 7.5 — Agent Skills */}
        <AgentSkillsSection agentSlug="e-commerce" agentName="E-Commerce" />

        {/* Section 8 — Final CTA */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <div className="border border-amber-500/20 rounded-xl p-12 text-center bg-gradient-to-b from-amber-500/[0.03] to-transparent">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">{t('ecommerce.never_miss_a_customer_message_again')}</h2>
            <p className="text-zinc-500 mb-10 max-w-lg mx-auto">
              {t('ecommerce_page.final_cta_desc')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/signup?agent=ecommerce"
                className="bg-amber-500 hover:bg-amber-400 text-black px-8 py-3.5 font-bold uppercase tracking-wider text-sm transition-colors"
              >
                {t('ecommerce_page.activate_ecommerce_button')}
              </Link>
            </div>
          </div>
        </section>

        {/* Footer verticals nav */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-12 border-t border-zinc-800">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-600 mb-6">{t('ecommerce.other_marketplace_agents')}</p>
          <div className="flex flex-wrap gap-4">
            {[
              { name: t('ecommerce_page.link_customer_support'), href: "/marketplace/customer-support" },
              { name: t('ecommerce_page.link_real_estate'), href: "/marketplace/real-estate" },
              {
                name: t('ecommerce_page.link_marketing_agencies'),
                href: "/marketplace/marketing-agencies",
              },
              { name: t('ecommerce_page.link_consultancy'), href: "/marketplace/consultancy" },
            ].map((v) => (
              <Link
                key={v.name}
                to={v.href}
                className="text-sm text-zinc-500 hover:text-amber-500 font-mono transition-colors"
              >
                {v.name} →
              </Link>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
